// ProfileTabPanel.tsx
import React, { useEffect, useState } from "react"
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import { KYCPlaceholder } from "./styles"
import DensityMediumIcon from "@mui/icons-material/DensityMedium"
import {
  fetchBookingDetails,
  resetBookingDetails,
  selectBookingHistory,
} from "../../slices/bookingHistorySlice"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  fetchRoles,
  selectUsers,
  updateUserRole,
} from "../../slices/usersSlice"
import {
  ResetKYCService,
  updateUserDetailsService,
} from "../../services/userService"
import { UserRole, UserStatus } from "../../constants/data"
import { selectLogin } from "../../slices/loginSlice"
import { staticRelativeImageHandler } from "../../utils/imageHandler"
import { showToast } from "../../slices/toastSlice"

type ProfileTabPanelProps = {
  isSmallScreen: boolean
  userId: string | undefined
}

interface ProfileDetails {
  firstName: string
  lastName: string
  email: string
  mobile: string
  role: string
  updatedStatus: string
}

export const ProfileImage = ({
  imageUrl,
  styles = {},
}: {
  imageUrl: string | null
  styles?: Record<string, unknown>
}) => {
  const commonStyles = {
    marginTop: "10px",
    borderRadius: "50%",
    width: "120px",
    height: "120px",
    alignSelf: "center",
  }

  if (imageUrl) {
    return (
      <img
        src={staticRelativeImageHandler(imageUrl)}
        alt="Profile Pic"
        style={{ ...commonStyles, ...styles }}
      />
    )
  } else {
    return (
      <AccountCircleIcon
        style={{ ...commonStyles, ...styles }}
        color="primary"
      />
    )
  }
}

const ProfileTabPanel: React.FC<ProfileTabPanelProps> = ({
  isSmallScreen,
  userId,
}) => {
  const [resetModal, setResetModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const dispatch = useAppDispatch()
  const { bookingDetails, isDetailsLoading } =
    useAppSelector(selectBookingHistory)
  const { roles } = useAppSelector(selectUsers)
  const { role } = useAppSelector(selectLogin)

  const resetKYC = async () => {
    if (userId)
      try {
        setLoading(true)
        await ResetKYCService(userId as string)

        dispatch(
          showToast({
            toastType: "success",
            msg: "Reset Successful",
          }),
        )
        setLoading(false)
        dispatch(fetchBookingDetails({ userId: userId as string }))
      } catch (err) {
        setLoading(false)
        dispatch(
          showToast({
            toastType: "Error",
            msg: "Reset Failed",
          }),
        )
      }
  }

  useEffect(() => {
    setProfileDetails({
      firstName: bookingDetails.first_name || "",
      lastName: bookingDetails.last_name || "",
      email: bookingDetails.email || "",
      mobile: bookingDetails.mobile || "",
      role:
        bookingDetails.roles.length > 0
          ? bookingDetails.roles[0]?.id || ""
          : "",
      updatedStatus: bookingDetails.status,
    })
  }, [bookingDetails])

  const [profileDetails, setProfileDetails] = useState<ProfileDetails>({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    role: "",
    updatedStatus: "",
  })

  useEffect(() => {
    dispatch(fetchRoles())
  }, [dispatch])

  useEffect(() => {
    if (userId) dispatch(fetchBookingDetails({ userId }))
    return () => {
      dispatch(resetBookingDetails())
    }
  }, [dispatch, userId])

  // input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    // Check if the value contains only alphabetic characters
    if (/^[A-Za-z]*$/.test(value) || value === "") {
      setProfileDetails((prevValues) => ({
        ...prevValues,
        [name]: value,
      }))
    }
  }

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target

    setProfileDetails((prevValues) => ({
      ...prevValues,
      updatedStatus: value,
    }))
  }
  const handleUserRoleChange = (selectedValue: string | undefined) => {
    const roleId = selectedValue || ""

    setProfileDetails((prevValues) => ({
      ...prevValues,
      role: roleId,
    }))
  }

  const isSaveDisabled =
    profileDetails.firstName.trim() === "" ||
    profileDetails.lastName.trim() === "" ||
    role === UserRole.AUDITOR

  const handleSave = () => {
    // Perform saving logic
    updateUserDetailsService({
      payload: {
        userId: bookingDetails.id.toString(),
        ...profileDetails,
        page: 0,
        pageSize: 0,
      },
    })

    dispatch(
      updateUserRole({
        userId: bookingDetails.id.toString(),
        roleId: profileDetails.role,
        page: 0,
        pageSize: 0,
      }),
    )
  }

  return (
    <Box>
      {isDetailsLoading ? (
        <Stack gap={2}>
          <Skeleton
            variant="circular"
            height={120}
            width={120}
            sx={{ alignSelf: "center" }}
          />
          <Skeleton variant="rectangular" height={50} />
          <Skeleton variant="rectangular" height={50} />
          <Skeleton variant="rectangular" height={50} />
          <Skeleton variant="rectangular" height={50} />
          <Skeleton variant="rectangular" height={150} />
        </Stack>
      ) : (
        <Stack gap={2} paddingBottom={"20px"}>
          <ProfileImage
            imageUrl={staticRelativeImageHandler(bookingDetails.profile_image)}
          />
          <Box
            sx={{
              display: "grid",
              columnGap: 3,
              rowGap: 2,
              gridTemplateColumns: !isSmallScreen ? "repeat(2, 9fr)" : "1fr",
              width: "100%",
            }}
          >
            <TextField
              label="First Name"
              name="firstName"
              size="small"
              fullWidth
              value={`${profileDetails.firstName}`}
              onChange={handleInputChange}
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              size="small"
              fullWidth
              value={`${profileDetails.lastName}`}
              onChange={handleInputChange}
              required
            />

            <TextField
              label="Email"
              name="email"
              size="small"
              fullWidth
              value={profileDetails.email || ""}
              aria-readonly
            />
            <TextField
              label="Mobile"
              name="mobile"
              size="small"
              fullWidth
              value={profileDetails.mobile || ""}
              aria-readonly
            />
            <FormControl size="small">
              <InputLabel id="role-type">Role</InputLabel>
              <Select
                labelId="role-type"
                id="role"
                label="Role"
                value={profileDetails.role || ""}
                onChange={(event) => {
                  handleUserRoleChange(event.target.value)
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="status-type">Status</InputLabel>
              <Select
                labelId="status-type"
                id="status-type-id"
                value={profileDetails.updatedStatus}
                label="Status"
                onChange={handleStatusChange}
              >
                {Object.values(UserStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() +
                      status.replace(/_/g, " ").slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <div style={{ display: "flex", gap: 20, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              color="error"
              disabled={loading}
              onClick={() => setResetModal(true)}
            >
              RESET KYC
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              SAVE
            </Button>
          </div>

          {bookingDetails.kyc_image ? (
            <img
              src={staticRelativeImageHandler(bookingDetails.kyc_image)}
              alt={`Profile Pic`}
              style={{
                maxHeight: 300,
                borderRadius: "6px",
                objectFit: "contain",
                width: "100%",
              }}
            />
          ) : (
            <KYCPlaceholder
              direction={!isSmallScreen ? "row" : "column"}
              alignItems={isSmallScreen ? "center" : ""}
            >
              <AccountCircleIcon
                color="primary"
                style={{ height: "100px", width: "100px" }}
              />
              <div>
                {[1, 2, 3].map((key) => (
                  <DensityMediumIcon
                    key={key}
                    style={{
                      height: "100px",
                      width: "100px",
                      marginLeft: key === 1 ? "0" : "-26px",
                      color: "#e4e4e4",
                    }}
                  />
                ))}
              </div>
            </KYCPlaceholder>
          )}
          <Modal open={resetModal} onClose={() => setResetModal(false)}>
            <Box
              style={{ width: "100%", maxWidth: "500px" }}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 400,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Typography
                id="modal-modal-title"
                variant="h5"
                component="h2"
                textAlign="center"
              >
                Confirm KYC RESET?
              </Typography>

              <Typography
                id="modal-modal-title"
                variant="subtitle1"
                component="h2"
                textAlign="center"
              >
                Confirm KYC - RESET for this {profileDetails.firstName} -{" "}
                {profileDetails.lastName} ({profileDetails.mobile})?
              </Typography>
              <div
                style={{
                  display: "flex",
                  gap: 20,
                  justifyContent: "center",
                }}
              >
                <Button variant="outlined" onClick={() => setResetModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={loading}
                  onClick={() => {
                    resetKYC()
                    setResetModal(false)
                  }}
                >
                  RESET KYC
                </Button>
              </div>
            </Box>
          </Modal>
        </Stack>
      )}
    </Box>
  )
}

export default ProfileTabPanel
